import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  Typography,
} from '@mui/material';
import LayersClearIcon from '@mui/icons-material/LayersClear';

import { OperationService } from '../../../services';
import type {
  ImageLabImageFormat,
  OperationResult,
  SourceImageRef,
} from '../../../domain';
import {
  DEFAULT_REMOVE_BACKGROUND_STATE,
  buildRemoveBackgroundRequest,
  hasSourceImage,
  type RemoveBackgroundFormState,
} from './buildRequests';
import { useOperationMutation } from './useOperationMutation';
import { submitErrorMessage } from '../../../components/auth/signInRequiredError';
import { OperationFailedError } from './operationFailedError';
import type { StudioParams } from '../../shared/panels/swatchSelection';
import { StudioSwatchChips } from './StudioSwatchChips';
import { ProviderModelPicker } from './ProviderModelPicker';

interface Props {
  ensureSession: () => Promise<string>;
  isCreatingSession?: boolean;
  source: SourceImageRef | undefined;
  /** RemoveBackground has no prompt field; chip row is informational. */
  studioParams?: StudioParams;
  onResult?: (result: OperationResult) => void;
}

const FORMAT_OPTIONS: Array<{ value: ImageLabImageFormat; label: string }> = [
  { value: 'Png', label: 'PNG (transparent)' },
  { value: 'Webp', label: 'WebP (smaller)' },
  { value: 'Jpeg', label: 'JPEG (no transparency)' },
];

export const RemoveBackgroundPanel: React.FC<Props> = ({
  ensureSession,
  isCreatingSession = false,
  source,
  studioParams,
  onResult,
}) => {
  const [state, setState] = useState<RemoveBackgroundFormState>(DEFAULT_REMOVE_BACKGROUND_STATE);
  const [validationError, setValidationError] = useState<string | null>(null);

  const mutation = useOperationMutation(OperationService.removeBackground);

  const hasSource = hasSourceImage(source);

  const handleSubmit = async () => {
    if (!source) {
      setValidationError('A source image is required to remove background.');
      return;
    }
    try {
      const sid = await ensureSession();
      const request = buildRemoveBackgroundRequest(state, sid, source);
      setValidationError(null);
      const result = await mutation.mutateAsync(request);
      onResult?.(result);
    } catch (err) {
      // BE-reported operation failures are already surfaced via the
      // `mutation.isError` Alert below — don't double-render them as a
      // validation warning. Only treat real validation/network errors
      // (from `buildRequest` or `ensureSession`) as warnings.
      if (err instanceof OperationFailedError) return;
      setValidationError(submitErrorMessage(err));
    }
  };

  const submitDisabled = mutation.isPending || isCreatingSession || !hasSource;

  return (
    <Stack spacing={1.5}>
      <Stack direction="row" spacing={1} alignItems="center">
        <LayersClearIcon fontSize="small" />
        <Typography variant="subtitle2">Remove background</Typography>
      </Stack>

      {!hasSource && (
        <Alert severity="info">Add or select a source image to remove its background.</Alert>
      )}

      <StudioSwatchChips studioParams={studioParams} hint="not applied to Remove background" />

      <ProviderModelPicker
        model={state.model}
        onModelChange={m => setState(s => ({ ...s, model: m }))}
        disabled={mutation.isPending || isCreatingSession}
        label="Image model"
        opKind="RemoveBackground"
      />

      <FormControl size="small" fullWidth>
        <InputLabel id="rb-format-label">Output format</InputLabel>
        <Select
          labelId="rb-format-label"
          label="Output format"
          value={state.outputFormat}
          onChange={e => setState(s => ({ ...s, outputFormat: e.target.value as ImageLabImageFormat }))}
        >
          {FORMAT_OPTIONS.map(opt => (
            <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControlLabel
        control={
          <Switch
            checked={state.refineMask}
            onChange={e => setState(s => ({ ...s, refineMask: e.target.checked }))}
            size="small"
          />
        }
        label="Refine edges"
      />

      {validationError && <Alert severity="warning">{validationError}</Alert>}
      {mutation.isError && (
        <Alert severity="error">
          {mutation.error instanceof OperationFailedError ? (
            <>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {mutation.error.message}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {mutation.error.code}
                {mutation.error.retryable ? ' · retryable' : ''}
              </Typography>
            </>
          ) : (
            mutation.error instanceof Error ? mutation.error.message : 'Remove background failed.'
          )}
        </Alert>
      )}

      <Box>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={submitDisabled}
          startIcon={
            mutation.isPending || isCreatingSession ? (
              <CircularProgress size={16} />
            ) : (
              <LayersClearIcon />
            )
          }
        >
          {isCreatingSession
            ? 'Starting session…'
            : mutation.isPending
              ? 'Removing…'
              : 'Remove background'}
        </Button>
      </Box>
    </Stack>
  );
};
